import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, User, Info, X, StopCircle } from 'lucide-react';
import { useEffect, useRef, useState, memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AIAvatar } from './AIAvatar';
import { PersonaInfo } from './PersonaInfo';
import {
  InlineRealtimeFeedback,
  InlineFeedbackTip,
} from './InlineRealtimeFeedback';
import { TtsConfigPanel, type TtsConfig } from './TtsConfigPanel';

interface ConversationAreaProps {
  transcript: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
  isAISpeaking: boolean;
  persona: any;
  className?: string;
  isPracticeStarted: boolean;
  onStartPractice: () => void;
  onEndSession?: () => void;
  sessionId?: string | null;
  hasCompetitorData?: boolean;
  engine?: 'elevenlabs' | 'openai' | 'livekit';
  isConnecting?: boolean;
  scenarioCode?: string | null;
  ttsConfig?: TtsConfig;
  onTtsConfigChange?: (config: TtsConfig) => void;
}

// Define reverse role scenarios that don't need feedback
const REVERSE_ROLE_SCENARIOS = [
  'prudential-malaysia-3',
  'prudential-malaysia-4',
  'prudential-prusignature-plus-reverse-en',
  'prudential-prusignature-plus-reverse-ms',
];

// Memoized message rendering component to prevent re-renders during feedback updates
const MessageList = memo(
  ({
    transcript,
    isAISpeaking,
    feedbackTips,
  }: {
    transcript: Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>;
    isAISpeaking: boolean;
    feedbackTips: Array<{
      type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
      message: string;
      timestamp: Date;
      stage?: string;
      messageIndex: number;
    }>;
  }) => {
    return (
      <AnimatePresence>
        {transcript.map((message, index) => (
          <div key={`message-${index}`}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`flex ${
                message.source === 'ai' ? 'justify-start' : 'justify-end'
              }`}
            >
              <div
                className={`max-w-[85%] lg:max-w-[80%] rounded-lg p-3 lg:p-4 flex items-start space-x-2 lg:space-x-3
                  ${
                    message.source === 'ai'
                      ? 'bg-white shadow-sm'
                      : 'bg-primary text-white'
                  }`}
              >
                {message.source === 'ai' ? (
                  <Bot className="w-4 lg:w-5 h-4 lg:h-5 mt-1 flex-shrink-0" />
                ) : (
                  <User className="w-4 lg:w-5 h-4 lg:h-5 mt-1 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="text-xs lg:text-sm whitespace-pre-wrap">
                    {message.message.replace(/\n{3,}/g, '\n\n')}
                  </p>
                  <span className="text-[10px] lg:text-xs opacity-60 mt-1 block">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Render feedback tips after user messages */}
            {message.source === 'user' &&
              feedbackTips.filter((tip) => tip.messageIndex === index).length >
                0 && (
                <div className="relative -mt-1">
                  <AnimatePresence mode="popLayout">
                    {feedbackTips
                      .filter((tip) => tip.messageIndex === index)
                      .map((tip, tipIndex) => (
                        <InlineFeedbackTip
                          key={`tip-${index}-${tipIndex}`}
                          tip={tip}
                          delay={tipIndex * 0.08}
                        />
                      ))}
                  </AnimatePresence>
                </div>
              )}
          </div>
        ))}

        {isAISpeaking && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex justify-start"
          >
            <div className="bg-white shadow-sm rounded-lg p-3 lg:p-4 flex items-center space-x-2 lg:space-x-3">
              <Bot className="w-4 lg:w-5 h-4 lg:h-5 text-gray-400" />
              <div className="flex space-x-1">
                <motion.div
                  className="w-2 h-2 rounded-full bg-gray-400"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity }}
                />
                <motion.div
                  className="w-2 h-2 rounded-full bg-gray-400"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: 0.2,
                  }}
                />
                <motion.div
                  className="w-2 h-2 rounded-full bg-gray-400"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: 0.4,
                  }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
  // Custom comparison function - only re-render if transcript or isAISpeaking changes
  (prevProps, nextProps) => {
    return (
      prevProps.transcript === nextProps.transcript &&
      prevProps.isAISpeaking === nextProps.isAISpeaking &&
      prevProps.feedbackTips.length === nextProps.feedbackTips.length
    );
  },
);

MessageList.displayName = 'MessageList';

export function ConversationArea({
  transcript,
  isAISpeaking,
  persona,
  className,
  isPracticeStarted,
  onStartPractice,
  onEndSession,
  sessionId,
  hasCompetitorData = false,
  engine = 'elevenlabs',
  isConnecting = false,
  scenarioCode = null,
  ttsConfig,
  onTtsConfigChange,
}: ConversationAreaProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [feedbackTips, setFeedbackTips] = useState<
    Array<{
      type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
      message: string;
      timestamp: Date;
      stage?: string;
      messageIndex: number;
    }>
  >([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Extract the conversation UI into a separate function
  const renderConversation = () => {
    return (
      <div className={`relative h-full ${className}`}>
        {/* Client Info Button - Moved z-index up to ensure it's always on top */}
        <div className="absolute top-2 right-2 lg:top-4 lg:right-4 z-30">
          <button
            onClick={() => setShowClientDetails(!showClientDetails)}
            className={`p-1.5 lg:p-2 rounded-full ${
              showClientDetails ? 'bg-slate-200' : 'bg-primary text-white'
            } shadow-md transition-colors`}
            title={t('custom.roleplay.conversation.toggleClientDetails')}
          >
            {showClientDetails ? (
              <X className="w-4 lg:w-5 h-4 lg:h-5" />
            ) : (
              <Info className="w-4 lg:w-5 h-4 lg:h-5" />
            )}
          </button>
        </div>

        {/* Client Details Panel - Fixed position instead of absolute, higher z-index, and proper top position */}
        <AnimatePresence>
          {showClientDetails && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-16 lg:top-20 right-2 lg:right-4 z-20 w-[calc(100vw-1rem)] lg:w-80 max-w-sm max-h-[calc(100vh-5rem)] bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <PersonaInfo persona={persona} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Avatar - Fixed position at top - Hidden on mobile during practice */}
        <div
          className={`${
            isPracticeStarted ? 'lg:sticky' : 'sticky'
          } top-0 bg-white/95 backdrop-blur-sm z-10 mb-4 shadow-sm`}
        >
          <div className="flex items-center px-4 lg:px-0 lg:justify-center py-3 lg:py-6">
            <AIAvatar isAISpeaking={isAISpeaking} persona={persona} />

            <div className="ml-2 lg:ml-4 flex flex-col justify-center flex-1">
              <h3 className="text-sm lg:text-lg font-semibold text-slate-800">
                {persona?.profile?.name}, {persona?.profile?.age}
              </h3>
              <p className="text-[11px] lg:text-sm text-slate-500">
                {persona?.profile?.occupation}
              </p>
              {scenarioCode !== 'aia-2' && persona?.profile?.income && <p className="text-[11px] lg:text-sm text-slate-500">
                {lang === 'ko' ? '₩' : '$'}
                {persona?.profile?.income}/{t('custom.roleplay.persona.year')}
              </p>}
            </div>
          </div>
        </div>

        {/* Mobile End Session Button - At top of conversation */}
        {isPracticeStarted && onEndSession && (
          <div className="lg:hidden sticky top-0 bg-white z-30 border-b border-gray-200 mb-4">
            <div className="px-4 py-3">
              <button
                onClick={onEndSession}
                className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center shadow-sm"
              >
                <StopCircle className="w-4 h-4 mr-2" />
                <span>{t('custom.roleplay.buttons.endSession')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Conversation Messages */}
        <div className="max-w-3xl mx-auto space-y-3 lg:space-y-4 px-4 lg:px-0 pt-2 lg:pt-4 pb-20 lg:pb-4">
          <MessageList
            transcript={transcript}
            isAISpeaking={isAISpeaking}
            feedbackTips={feedbackTips}
          />
          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  };

  useEffect(() => {
    scrollToBottom();
  }, [transcript, feedbackTips]);

  // Check if this is a reverse role scenario
  const isReverseRole = REVERSE_ROLE_SCENARIOS.includes(scenarioCode || '');

  // Only initialize the feedback analyzer if practice is started and we have a session ID
  // AND it's NOT a reverse role scenario
  if (isPracticeStarted && sessionId) {
    return (
      <>
        {!isReverseRole && (
          <InlineRealtimeFeedback
            transcript={transcript}
            sessionId={sessionId}
            hasCompetitorData={hasCompetitorData}
            setFeedbackTips={setFeedbackTips}
          />
        )}
        {renderConversation()}
      </>
    );
  }

  if (!isPracticeStarted) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="flex-1 flex flex-col items-center justify-center pb-20 lg:pb-4">
          <div className="w-full max-w-4xl flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 bg-white rounded-xl shadow-sm p-4 lg:p-6 mb-6 lg:mb-8">
            <div className="md:w-1/3 flex-shrink-0 mx-auto md:mx-0">
              <AIAvatar isAISpeaking={false} persona={persona} />
            </div>

            <div className="md:w-2/3 w-full flex flex-col overflow-hidden">
              <div className="h-[300px] lg:h-[400px] flex flex-col">
                <PersonaInfo persona={persona} />
              </div>
            </div>
          </div>

          <div className="text-center max-w-lg px-4">
            <h2 className="text-xl lg:text-2xl font-semibold mb-3 lg:mb-4 text-slate-800">
              {t('custom.roleplay.conversation.readyToPractice')}
            </h2>
            <p className="text-sm lg:text-base text-slate-600 mb-4 lg:mb-6 break-words">
              <Trans
                i18nKey={
                  persona?.profile?.location
                    ? 'custom.roleplay.conversation.speakingWith'
                    : 'custom.roleplay.conversation.speakingWithNoLocation'
                }
                values={{
                  name: persona?.profile?.name,
                  age: persona?.profile?.age,
                  occupation: persona?.profile?.occupation,
                  location: persona?.profile?.location || '',
                }}
                components={{
                  bold: <span className="font-medium text-slate-700" />,
                }}
              />
            </p>
            <Button
              onClick={onStartPractice}
              size="lg"
              disabled={isConnecting}
              className="w-full md:w-auto px-6 lg:px-8 bg-primary hover:bg-primary-dark text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting
                ? t('custom.roleplay.buttons.connecting') || 'Connecting...'
                : t('custom.roleplay.buttons.startPractice')}
            </Button>

            {scenarioCode === 'aia-2' && ttsConfig && onTtsConfigChange && (
              <TtsConfigPanel
                ttsConfig={ttsConfig}
                onTtsConfigChange={onTtsConfigChange}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // For non-practice mode, return the conversation UI
  return renderConversation();
}
